import React, { useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ASG_CHIP, docErr } from "@/lib/docApi";
import { Chip, Empty, ErrorBox, Loading, btnGold, field, useLoad, when } from "@/components/nds/windows/finance/parts";

export const SCRIPT_FONT = { fontFamily: "'Great Vibes', cursive" };

export const StatusChip = ({ map, status, testid }) => {
  const [tone, label] = map[status] || ["grey", status];
  return <Chip tone={tone} data-testid={testid}>{label}</Chip>;
};

export function History({ rec, testid }) {
  return (
    <ol data-testid={testid} className="space-y-1.5">
      {[...(rec.history || [])].reverse().map((h, i) => (
        <li key={i} className="text-xs text-nex-muted flex gap-2">
          <span className="font-mono text-[10px] shrink-0 w-36">{when(h.at)}</span>
          <span className="text-nex-silver">{h.from_status ? `${h.from_status} → ` : ""}{h.to_status}</span>
          <span className="truncate">{h.note}{h.actor?.name ? ` · ${h.actor.name}` : ""}</span>
        </li>
      ))}
    </ol>
  );
}

export const DocBody = ({ text, testid }) => (
  <div data-testid={testid} className="whitespace-pre-wrap text-sm leading-relaxed text-white/85 bg-black/30 border border-white/10 rounded-sm p-4 max-h-[46vh] overflow-y-auto">{text}</div>
);

/** Typed-name signature with live script preview + explicit adoption; acknowledgment for non-signature types. */
export function SignPanel({ assignment, onSign, onAck, prefix = "doc-sign" }) {
  const [name, setName] = useState("");
  const [adopted, setAdopted] = useState(false);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  if (assignment.assignment_type !== "signature_required") {
    return (
      <button type="button" disabled={busy} onClick={() => run(onAck)} data-testid={`${prefix}-acknowledge`} className={btnGold}>
        {busy ? "Saving…" : assignment.assignment_type === "for_record" ? "Mark as received" : "I acknowledge this document"}
      </button>
    );
  }
  return (
    <div data-testid={`${prefix}-panel`} className="space-y-3 border border-white/10 rounded-sm p-4">
      <label className="block text-xs text-nex-muted">Type your full legal name
        <input data-testid={`${prefix}-name`} value={name} onChange={(e) => { setName(e.target.value); setAdopted(false); }}
          className={`${field} w-full mt-1`} placeholder="Full name" />
      </label>
      {name.trim().length >= 2 && (
        <div data-testid={`${prefix}-preview`} className="border-b border-white/30 pb-1 text-4xl text-white" style={SCRIPT_FONT}>{name}</div>
      )}
      <label className="flex items-center gap-2 text-sm text-white/85 cursor-pointer">
        <input type="checkbox" data-testid={`${prefix}-adopt`} checked={adopted} disabled={name.trim().length < 2}
          onChange={(e) => setAdopted(e.target.checked)} className="accent-[#d4a017]" />
        I adopt this as my legal signature.
      </label>
      <button type="button" data-testid={`${prefix}-confirm`} disabled={!adopted || busy} onClick={() => run(() => onSign(name, adopted))}
        className={btnGold}>{busy ? "Signing…" : "Sign document"}</button>
    </div>
  );
}

/** One assignment: content, PDF, sign/acknowledge. `path` is the recipient view route (…/assignments/{id} or /public/{token}). */
export function RecipientDocView({ api, path, onBack, onDone, prefix = "doc-recipient" }) {
  const [state] = useLoad(() => api.get(path), [path]);
  const [result, setResult] = useState(null);
  if (state.loading && !state.data) return <Loading testid={`${prefix}-loading`} />;
  if (state.error) return <ErrorBox testid={`${prefix}-error`} error={state.error} />;
  const { document: d } = state.data;
  const a = result ? { ...state.data.assignment, status: result.status } : state.data.assignment;
  const signature = result?.signature || state.data.signature;
  const done = !["pending", "viewed"].includes(a.status);
  const finish = async (fn, msg, status) => {
    try { const res = await fn(); toast.success(msg); setResult({ status, signature: status === "signed" ? res : null }); onDone?.(); }
    catch (e) { toast.error(docErr(e)); }
  };
  return (
    <div data-testid={`${prefix}-view`} className="space-y-4">
      {onBack && <button type="button" onClick={onBack} data-testid={`${prefix}-back`} className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold"><ArrowLeft className="h-3 w-3" /> All documents</button>}
      <div className="flex flex-wrap items-center gap-3">
        <h3 data-testid={`${prefix}-title`} className="text-lg font-semibold text-white">{d.title}</h3>
        <StatusChip map={ASG_CHIP} status={a.status} testid={`${prefix}-status`} />
        {!(done && path.startsWith("/public/")) && <button type="button" onClick={() => api.openPdf(`${path}/pdf`).catch((e) => toast.error(docErr(e)))} data-testid={`${prefix}-pdf`} className={`${btnGold} ml-auto inline-flex items-center gap-1.5`}><FileText className="h-3 w-3" /> View PDF</button>}
      </div>
      <p className="text-xs text-white/50">{d.document_type} · for {a.recipient_name}{!done && a.token_expires_at ? ` · available until ${when(a.token_expires_at)}` : ""}</p>
      <DocBody text={d.final_content} testid={`${prefix}-content`} />
      {done ? (
        <div data-testid={`${prefix}-done`} className="flex items-center gap-2 text-emerald-300 text-sm">
          <CheckCircle2 className="h-4 w-4" />
          {signature ? <span>Signed as <span className="text-2xl text-white" style={SCRIPT_FONT}>{signature.typed_name}</span> · {when(signature.signed_at)}</span> : <span>Completed ({a.status}).</span>}
        </div>
      ) : (
        <SignPanel assignment={a} prefix={`${prefix}-sign`}
          onSign={(typed_name, adopted) => finish(() => api.post(`${path}/sign`, { typed_name, adopted }), "Document signed", "signed")}
          onAck={() => finish(() => api.post(`${path}/acknowledge`), "Acknowledged", "acknowledged")} />
      )}
    </div>
  );
}

/** Recipient inbox (staff My Documents, partner + resident portals): list → in-place RecipientDocView. */
export function RecipientInbox({ api, listPath, prefix = "doc-inbox" }) {
  const [selected, setSelected] = useState(null);
  const [state, load] = useLoad(() => api.get(listPath), [listPath]);
  if (selected) return <RecipientDocView api={api} path={`${listPath}/${selected}`} onBack={() => { setSelected(null); load(); }} prefix={`${prefix}-doc`} />;
  if (state.loading && !state.data) return <Loading testid={`${prefix}-loading`} />;
  if (state.error) return <ErrorBox testid={`${prefix}-error`} error={state.error} onRetry={load} />;
  const rows = state.data.assignments;
  if (!rows.length) return <Empty testid={`${prefix}-empty`}>No documents have been shared with you yet.</Empty>;
  return (
    <ul data-testid={`${prefix}-list`} className="divide-y divide-white/5 border border-white/10 rounded-sm">
      {rows.map((a) => (
        <li key={a.id}>
          <button type="button" onClick={() => setSelected(a.id)} data-testid={`${prefix}-row-${a.id}`} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-white/[0.03]">
            <span className="flex-1 min-w-0 truncate text-white/90">{a.document_title}</span>
            <span className="text-xs text-white/40 hidden sm:inline">{a.assignment_type.replace(/_/g, " ")}</span>
            <StatusChip map={ASG_CHIP} status={a.status} />
            <ArrowRight className="h-3.5 w-3.5 text-white/40" />
          </button>
        </li>
      ))}
    </ul>
  );
}

export const Spinner = () => <Loader2 className="h-4 w-4 animate-spin" />;
